import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';
import { communitiesApi } from '../api';
import DashboardLayout from '../components/dashboard/DashboardLayout';

/**
 * "Moja społeczność" — landing page po SSO z Sorto-CRM.
 *
 * Wywołuje GET /communities/my (auto-utworzona przez SSO-exchange w backendzie
 * dla org_id z JWT), potem przekierowuje do standardowego CommunityDetailPage
 * z preservacją ?embed=1.
 *
 * Gdy 404 (np. user bez sso_org_id) — pokazuje błąd z linkiem do listy communities.
 */
export default function MyCommunityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await communitiesApi.mine();
        const id = r.data?.id;
        if (!id) throw new Error('Backend zwrócił community bez id');
        const embed = params.get('embed');
        const filter = params.get('filter');
        const lang = params.get('lang');
        const sort = params.get('sort');
        const q = new URLSearchParams();
        if (embed) q.set('embed', embed);
        if (filter) q.set('filter', filter);
        if (lang) q.set('lang', lang);
        if (sort) q.set('sort', sort);
        const qs = q.toString() ? `?${q.toString()}` : '';
        navigate(`/dashboard/community/${id}${qs}`, { replace: true });
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) {
          setError('Nie masz jeszcze swojej społeczności. Skontaktuj się z administratorem.');
        } else {
          setError(e?.response?.data?.detail || e.message);
        }
      }
    })();
  }, []);

  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[400px]">
        {error ? (
          <div className="bg-white border border-rose-200 rounded-lg p-6 max-w-md text-center">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-rose-500" />
            <h2 className="font-semibold text-slate-900 mb-2">Społeczność niedostępna</h2>
            <p className="text-sm text-rose-600">{error}</p>
          </div>
        ) : (
          <div className="text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-2 text-indigo-500 animate-spin" />
            <p className="text-sm text-slate-500">Otwieram Twoją społeczność...</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
